import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import { blue, green, grey, orange, purple } from "@mui/material/colors";
import { ReactNode } from "react";
import { useCurrentUser } from "./CurrentUser";
import { useUIFlags } from "./UIFlags";
import { usePhone } from "./usePhone";

// WelcomeDialog introduces the app the first time a user opens it. An admin viewing
// as the user doesn't see it, since it's the user's to dismiss.
export function WelcomeDialog() {
    const me = useCurrentUser();
    const flags = useUIFlags();
    const phone = usePhone();

    if (!me || me.masqueradeBy || flags.has("welcome")) return null;

    const close = () => flags.set("welcome", true);

    return (
        <Dialog open onClose={close} maxWidth="sm" fullWidth>
            <DialogContent>
                <Typography variant="h6">Welcome to TSK</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Three things to know.
                </Typography>

                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: phone ? "1fr" : "repeat(3, 1fr)",
                    gap: phone ? 2 : 3,
                }}>
                    <Item art={<TaskArt />} title="Recurring tasks">
                        Give a task a day, or make it repeat, for example every Monday.
                    </Item>
                    <Item art={<ProjectArt />} title="Projects">
                        A group of tasks for one goal. Its colour marks each task.
                    </Item>
                    <Item art={<TeamArt />} title="Teams">
                        The people who share your projects. {phone
                            ? "Tap the team name at the top to change teams."
                            : "Change teams in the sidebar."}
                    </Item>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={close}>Get started</Button>
            </DialogActions>
        </Dialog>
    );
}

function Item(props: { art: ReactNode; title: string; children: ReactNode }) {
    const phone = usePhone();

    return (
        <Box sx={{
            display: "flex",
            flexDirection: phone ? "row" : "column",
            alignItems: phone ? "center" : "stretch",
            gap: phone ? 2 : 1,
        }}>
            <Box sx={{
                flexShrink: 0,
                width: phone ? 96 : "100%",
                bgcolor: grey[100],
                borderRadius: 2,
                "& svg": { display: "block", width: "100%", height: "auto" },
            }}>
                {props.art}
            </Box>
            <Box>
                <Typography variant="subtitle2">{props.title}</Typography>
                <Typography variant="body2" color="text.secondary">{props.children}</Typography>
            </Box>
        </Box>
    );
}

function TaskArt() {
    return (
        <svg viewBox="0 0 120 80" aria-hidden>
            <rect x="16" y="18" width="88" height="20" rx="4" fill="white" stroke={grey[300]} />
            <circle cx="28" cy="28" r="5" fill={green[500]} />
            <path d="M25.5 28l2 2 3.5-4" stroke="white" strokeWidth="1.5" fill="none" />
            <rect x="38" y="25" width="44" height="6" rx="3" fill={grey[300]} />

            <rect x="16" y="44" width="88" height="20" rx="4" fill="white" stroke={grey[300]} />
            <circle cx="28" cy="54" r="5" fill="none" stroke={grey[400]} strokeWidth="1.5" />
            <rect x="38" y="51" width="44" height="6" rx="3" fill={grey[400]} />
            <path d="M89 51.5a5 5 0 0 1 9 1.5M98 56.5a5 5 0 0 1 -9 -1.5" stroke={blue[500]}
                  strokeWidth="1.5" fill="none" strokeLinecap="round" />
            <path d="M98 49.5v3.5h-3.5M89 58.5v-3.5h3.5" stroke={blue[500]}
                  strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ProjectArt() {
    const rows = [blue[500], blue[500], orange[500]];

    return (
        <svg viewBox="0 0 120 80" aria-hidden>
            {rows.map((color, i) => (
                <g key={i} transform={`translate(0 ${12 + i * 20})`}>
                    <rect x="16" y="0" width="88" height="16" rx="4" fill="white" stroke={grey[300]} />
                    <rect x="16" y="0" width="4" height="16" rx="2" fill={color} />
                    <rect x="26" y="5" width="12" height="6" rx="2" fill={color} />
                    <rect x="44" y="5" width={i === 1 ? 32 : 48} height="6" rx="3" fill={grey[300]} />
                </g>
            ))}
        </svg>
    );
}

function TeamArt() {
    const people = [blue[400], purple[400], green[400]];

    return (
        <svg viewBox="0 0 120 80" aria-hidden>
            {people.map((color, i) => (
                <g key={i} transform={`translate(${36 + i * 24} 40)`}>
                    <circle r="15" fill={color} stroke={grey[100]} strokeWidth="3" />
                    <circle cy="-4" r="5" fill="white" />
                    <path d="M-8 9a8 7 0 0 1 16 0" fill="white" />
                </g>
            ))}
        </svg>
    );
}
